import React from "react";
import { Dropdown, Progress, Tooltip } from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { addRecentItem } from "utils/recentItems";
import { useAuth } from "contexts/AuthContext";
import { customStyles, statusColors } from "./TaskCard";

export const getStatus = (startDate, endDate) => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) return "UPCOMING";
  if (now > end) return "COMPLETED";
  return "ACTIVE";
};

export const statusStyles = {
  ACTIVE: {
    accent: "#dcfce7",
    text: "#166534",
    ring: "#bbf0d0",
  },
  UPCOMING: {
    accent: "#e0f2fe",
    text: "#0369a1",
    ring: "#b9e0f5",
  },
  COMPLETED: {
    accent: "#f3f4f6",
    text: "#6b7280",
    ring: "#d3d9dc",
  },
};

const priorityColors = ["#DBEAFE", "#FEF3C7", "#FEE2E2"];

export const getIterationColor = (id) => {
  const index = id % priorityColors.length;
  return priorityColors[index];
};

const IterationCard = ({
  index,
  iteration,
  projectId,
  view = "grid",
  selectedTags,
  onTagClick,
  onView,
  onEdit,
  onDelete,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const start = dayjs(iteration.startDate);
  const end = dayjs(iteration.endDate);
  const status = getStatus(iteration.startDate, iteration.endDate);
  const keyColor = getIterationColor(iteration.key.length);

  const items = [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "View",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onView?.();
      },
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onEdit?.();
      },
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      danger: true,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onDelete?.();
      },
    },
  ];

  const statusKeys = Object.keys(statusColors);
  const badgeStatus = statusKeys[index % statusKeys.length];
  const badgeStyle = statusColors[badgeStatus];

  const handleOpenIteration = () => {
    addRecentItem(user.id, {
      id: projectId,
      type: "iteration",
      title: iteration.name,
      path: `/general/projects/${projectId}/iterations/${iteration.id}`,
      openedAt: new Date(Date.now()).toISOString(),
    });

    navigate(`/general/projects/${projectId}/iterations/${iteration.id}`);
  };

  return (
    <div
      className={`iteration-card ${view}`}
      onClick={handleOpenIteration}
      style={{ cursor: "pointer" }}
    >
      <div
        className="iteration-accent"
        style={{ background: badgeStyle.border }}
      />

      {/* context menu */}
      <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
        <MoreOutlined
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: 16,
            position: "absolute",
            top: 20,
            right: 10,
          }}
        />
      </Dropdown>

      {/* ROW 1 */}
      <div className="iteration-row top-row">
        <div className="iteration-header">
          <span
            className="iteration-key"
            style={{
              ...customStyles,
              backgroundColor: badgeStyle.bg,
              color: badgeStyle.text,
              border: `1px solid ${badgeStyle.border}`,
            }}
          >
            {iteration.key}
          </span>
        </div>

        <h6 className="iteration-title text-xxs">{iteration.name}</h6>
      </div>

      {/* ROW 2 */}
      <div className="iteration-row bottom-row mt-2">
        <p className="project-description" style={{ display: "flex", gap: 3 }}>
          <span className="meta-icondd">
            <CalendarOutlined />
          </span>
          <span>
            {start.format("MMM DD")} – {end.format("MMM DD")}
          </span>
        </p>

        {iteration.tags.length > 0 && (
          <>
            <div className="iteration-divider" />
            <div className="iteration-tags">
              {iteration.tags.slice(0, 3).map((tag, index) => {
                const isActive = selectedTags.includes(tag.name);

                return (
                  <span
                    key={index}
                    className={`project-tag ${isActive ? "active-tag" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTagClick(tag.name);
                    }}
                  >
                    {tag.name}
                  </span>
                );
              })}

              {iteration.tags.length > 3 && (
                <Tooltip
                  title={iteration.tags
                    .slice(3)
                    .map((tag) => tag.name)
                    .join(", ")}
                >
                  <span className="project-tag more-tag">
                    +{iteration.tags.length - 3}
                  </span>
                </Tooltip>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IterationCard;
